import { Reveal, SectionHead, GoldBtn, PageHero, Particles } from "@/components/Shared";
import { ADDA_FEATURES, CONTACTS } from "@/data/content";

const STEPS = [
    { n: "01", title: "Register as Investor", text: "Tell us your thesis, sectors and ticket sizes." },
    { n: "02", title: "Get Curated Deal Flow", text: "Receive a matched shortlist from 120+ exhibiting startups." },
    { n: "03", title: "Meet at the Adda", text: "Speed meets, roundtables and closed-door dialogues across three days." },
    { n: "04", title: "Build Beyond the Bazaar", text: "Funding, mentorship and incubation pathways that outlast the festival." },
];

export default function InvestorAdda() {
    const investorContact = CONTACTS[1];
    return (
        <div data-testid="investor-adda-page">
            <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden noise"
                style={{ background: "linear-gradient(155deg, #1A1408 0%, #1A060C 50%, #140409 100%)" }}
                data-testid="adda-hero">
                <Particles density={40} />
                <div className="container-x relative">
                    <Reveal>
                        <span className="eyebrow">Investor Adda</span>
                        <h1 className="font-display font-medium text-4xl sm:text-6xl lg:text-7xl leading-[1.05] mt-7 max-w-4xl">
                            Where Conversations Become <span className="gold-text italic">Collaborations</span>
                        </h1>
                        <p className="mt-7 max-w-2xl text-muted-luxe text-base md:text-lg leading-relaxed">
                            The capital heart of Startup MahaBazaar — a dedicated zone where startups, investors,
                            family offices and corporates turn dialogue into deals.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <GoldBtn to="/register?type=investor" testid="adda-join-btn">Join Investor Adda</GoldBtn>
                            <GoldBtn to="/pitchyatra" variant="outline" testid="adda-pitchyatra-btn">Watch PitchYatra</GoldBtn>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="container-x">
                    <SectionHead eyebrow="Inside the Adda" title={<>Six Formats. <span className="gold-text">One Purpose — Capital.</span></>} />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {ADDA_FEATURES.map((f, i) => (
                            <Reveal key={f.title} delay={(i % 3) * 80}>
                                <div className="card-luxe tilt p-8 h-full" data-testid={`adda-feature-${i}`}>
                                    <div className="card-glow" />
                                    <div className="w-12 h-14 arch-top border border-[var(--line)] flex items-center justify-center">
                                        <f.icon size={19} strokeWidth={1.2} className="text-[var(--gold)]" />
                                    </div>
                                    <h3 className="font-display text-xl mt-6 text-[var(--ivory)]">{f.title}</h3>
                                    <p className="mt-3 text-sm text-muted-luxe leading-relaxed">{f.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60 noise relative overflow-hidden">
                <div className="container-x">
                    <SectionHead eyebrow="How It Works" title={<>From Handshake <span className="gold-text">to Term Sheet</span></>} />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 90}>
                                <div className="relative border hairline p-7 h-full hover:border-[var(--line)] transition-colors">
                                    <span className="font-display text-4xl gold-text">{s.n}</span>
                                    <h3 className="font-display text-lg mt-5 text-[var(--ivory)]">{s.title}</h3>
                                    <p className="mt-3 text-xs text-muted-luxe leading-relaxed">{s.text}</p>
                                    {i < STEPS.length - 1 && <span className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px shimmer-line" />}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
                    <Reveal>
                        <p className="font-display italic text-2xl md:text-3xl leading-relaxed text-[var(--champagne)]/90">
                            "Where conversations turn into collaborations — and collaborations into capital."
                        </p>
                        <div className="mt-10">
                            <GoldBtn to="/register?type=investor" testid="adda-register-bottom">Register as Investor</GoldBtn>
                        </div>
                    </Reveal>
                    <Reveal delay={140}>
                        <div className="card-luxe p-8">
                            <div className="card-glow" />
                            <p className="text-[0.6rem] tracking-[0.34em] uppercase text-[var(--gold)]">Investor Relations</p>
                            <h3 className="font-display text-2xl mt-3 text-[var(--ivory)]">{investorContact.name}</h3>
                            <p className="text-sm text-muted-luxe mt-1">{investorContact.role}</p>
                            <p className="text-sm text-muted-luxe mt-3 leading-relaxed">{investorContact.scope}</p>
                            <a href={`mailto:${investorContact.email}`} data-testid="adda-contact-email"
                                className="mt-6 inline-block text-sm text-[var(--champagne)] hover:text-[var(--gold)] transition-colors underline underline-offset-4 decoration-[rgba(201,162,75,0.4)]">
                                {investorContact.email}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
